import type { Annotation, UserVisibility } from "@/types/annotations";

import { parseKeypointAnnotations } from "./KeyPoints/helpers";
import type { ParsedKeypointAnnotation } from "./KeyPoints/types";
import { isManualBBoxAnnotation } from "./Segmentation/helpers";
import { getMeasurementAxisValue } from "@shared/types/measurementDescriptors";
import { buildMeasurementLineDrawSpecs } from "@/utils/annotationMeasurementUtils";

export const doNumberSetsMatch = (left: Set<number>, right: Set<number>): boolean => {
  if (left.size !== right.size) return false;

  for (const value of left) {
    if (!right.has(value)) return false;
  }

  return true;
};

export const buildSavedVisibleSegmentationIndices = (savedAnnotations: Annotation[]): Set<number> => {
  const visibleIndices = new Set<number>();

  savedAnnotations.forEach((annotation, index) => {
    if (annotation.userVisibility?.isVisible) {
      visibleIndices.add(index);
    }
  });

  return visibleIndices;
};

const buildParsedKeypointAnnotationSignature = (parsedAnnotations: ParsedKeypointAnnotation[]): string =>
  parsedAnnotations
    .map(({ id, keypoint, source, userVisibility, hiddenReason, visibleVisibility }) => {
      const normalizedUserVisibility: UserVisibility = userVisibility ?? {
        isVisible: hiddenReason === undefined && keypoint.visibility !== 0,
        ...(hiddenReason ? { hiddenReasons: [hiddenReason] } : {}),
      };
      return [
        id,
        keypoint.x,
        keypoint.y,
        keypoint.visibility,
        source,
        normalizedUserVisibility.isVisible ? "visible" : "hidden",
        normalizedUserVisibility.manualOverride === true ? "manual" : "automatic",
        [...(normalizedUserVisibility.hiddenReasons ?? [])].sort().join(","),
        hiddenReason ?? "",
        visibleVisibility ?? "",
      ].join(":");
    })
    .sort()
    .join("|");

export const haveParsedKeypointAnnotationsChangedSinceLastSave = (
  currentParsedAnnotations: ParsedKeypointAnnotation[],
  savedAnnotations: Annotation[]
): boolean =>
  buildParsedKeypointAnnotationSignature(currentParsedAnnotations) !==
  buildParsedKeypointAnnotationSignature(parseKeypointAnnotations(savedAnnotations));

export const hasSegmentationVisibilityChangedSinceLastSave = (
  currentVisibleIndices: Set<number>,
  savedAnnotations: Annotation[]
): boolean => !doNumberSetsMatch(currentVisibleIndices, buildSavedVisibleSegmentationIndices(savedAnnotations));

const formatBBox = (annotation: Annotation): string => {
  if (!annotation.bbox) return "";
  const { x, y, width, height } = annotation.bbox;
  return [x, y, width, height].map((value) => Number(value).toFixed(3)).join(",");
};

const formatMeasurement = (annotation: Annotation): string => {
  return buildMeasurementLineDrawSpecs(annotation.keypoints)
    .map((spec) => {
      const value = getMeasurementAxisValue(annotation.measurements, spec.key);
      const valuePart = typeof value === "number" ? value.toFixed(6) : "";
      return [
        spec.key,
        `${spec.start.name}=${Number(spec.start.x).toFixed(3)},${Number(spec.start.y).toFixed(3)}`,
        `${spec.end.name}=${Number(spec.end.x).toFixed(3)},${Number(spec.end.y).toFixed(3)}`,
        valuePart,
      ].join("#");
    })
    .join(";");
};

const buildSegmentationAnnotationSignature = (annotations: Annotation[]): string =>
  annotations
    .map((annotation) =>
      [
        annotation.id,
        annotation.category?.name ?? "",
        annotation.userVisibility?.isVisible !== false ? "visible" : "hidden",
        annotation.userVisibility?.manualOverride === true ? "manualOverride" : "automaticVisibility",
        [...(annotation.userVisibility?.hiddenReasons ?? [])].sort().join(","),
        isManualBBoxAnnotation(annotation) ? "manual" : "model",
        formatBBox(annotation),
        formatMeasurement(annotation),
      ].join(":")
    )
    .sort()
    .join("|");

export const haveSegmentationAnnotationsChangedSinceLastSave = (
  currentAnnotations: Annotation[],
  savedAnnotations: Annotation[]
): boolean =>
  buildSegmentationAnnotationSignature(currentAnnotations) !== buildSegmentationAnnotationSignature(savedAnnotations);
